import type { JSX } from 'react'

import { Icon, type IconName } from '../design-system/icon.tsx'
import { Link } from '../design-system/link.tsx'
import { cn } from '../lib/cn.ts'
import { normalizePath, type InternalPage } from '../lib/pages.ts'

/** The shared style for controls and links on the rail. */
export function rowClass(active: boolean): string {
  return cn(
    'text-sidebar-foreground flex items-center gap-3 px-3 py-2 text-sm',
    'hover:bg-sidebar-accent cursor-pointer',
    'text-left',
    active && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
  )
}

/** A group name as a `data-testid` fragment: lower-cased, spaces to hyphens. "CS Tools" becomes "cs-tools". */
export function slugifyGroup(group: string): string {
  return group.trim().toLowerCase().replace(/\s+/g, '-')
}

/** Every page in this console renders the same row; a group header and a flat entry both build one from it. */
export function NavRow({
  page,
  active,
  testId,
  onNavigate,
  icon,
  label = page.title
}: {
  page: InternalPage
  active: boolean
  testId: string
  onNavigate: () => void
  icon?: IconName
  label?: string
}): JSX.Element {
  return (
    <Link
      data-testid={testId}
      href={page.path}
      onNavigate={onNavigate}
      className={rowClass(active)}
      aria-current={active ? 'page' : undefined}
      // A short rail title repeats across sections ("Scorecard"), so a
      // screen reader's link list hears the full title instead.
      aria-label={label === page.title ? undefined : page.title}
    >
      {icon ? <Icon name={icon} className="size-4 shrink-0" /> : null}
      {label}
    </Link>
  )
}

/** The small heading over a rail list. A collapsible one is a button. */
const LIST_HEADING_CLASS =
  'text-sidebar-foreground px-3 py-2 text-xs font-semibold'

/**
 * A rail list's heading: plain text, or a button that collapses the list.
 *
 * The button keeps the heading's id, so the list it heads keeps its name.
 */
export function RailListHeading({
  id,
  label,
  collapsed,
  onToggleCollapsed
}: {
  id: string
  label: string
  /** Absent, the list does not collapse and the heading is plain text. */
  collapsed?: boolean
  onToggleCollapsed?: () => void
}): JSX.Element {
  if (onToggleCollapsed === undefined) {
    return (
      <p id={id} data-testid={id} className={LIST_HEADING_CLASS}>
        {label}
      </p>
    )
  }

  return (
    <button
      type="button"
      id={id}
      data-testid={id}
      aria-expanded={collapsed !== true}
      className={cn(
        LIST_HEADING_CLASS,
        'hover:bg-sidebar-accent flex cursor-pointer items-center justify-between text-left'
      )}
      onClick={onToggleCollapsed}
    >
      {label}
      <Icon
        name={collapsed === true ? 'chevron-right' : 'chevron-down'}
        className="size-3.5 shrink-0"
      />
    </button>
  )
}

/**
 * Starred or Recent: a heading and its rows, in stored order.
 *
 * An empty list renders nothing. Given `onToggleCollapsed`, the heading
 * collapses the rows and stays on the rail. A row uses the same short title
 * and accessible name as a section row.
 */
export function StoredPageList({
  pages,
  listId,
  heading,
  prefix,
  activePath,
  onNavigate,
  collapsed = false,
  onToggleCollapsed
}: {
  pages: readonly InternalPage[]
  listId: string
  heading: string
  prefix: string
  activePath: string
  onNavigate: (path: string) => void
  collapsed?: boolean
  onToggleCollapsed?: () => void
}): JSX.Element | null {
  if (pages.length === 0) return null
  const headingId = `${prefix}${listId}-heading`

  return (
    <div
      data-testid={`${prefix}${listId}`}
      role="group"
      aria-labelledby={headingId}
      className="flex flex-col gap-1"
    >
      <RailListHeading
        id={headingId}
        label={heading}
        collapsed={onToggleCollapsed === undefined ? undefined : collapsed}
        onToggleCollapsed={onToggleCollapsed}
      />
      {(collapsed ? [] : pages).map(page => (
        <NavRow
          key={page.id}
          page={page}
          icon={page.icon}
          label={page.railTitle ?? page.title}
          active={activePath === normalizePath(page.path)}
          testId={`${prefix}${listId}-${page.id}`}
          onNavigate={() => onNavigate(page.path)}
        />
      ))}
    </div>
  )
}
